/**
 * `LevelSourcesModal` — modale che mostra **tutti i Livelli** dichiarati per una
 * specifica skill, suddivisi per fonte: il proprio (Self), quello stimato
 * dall'AI e ogni singola revisione (Reviewer e Manager), **una riga per
 * revisione**. Le revisioni sono anonime: si mostra solo il ruolo del
 * valutatore e il Livello votato, mai l'identità.
 *
 * È lo "schemino" che si apre cliccando il contatore accanto al badge del
 * Livello (Self) nella `SkillAssessmentTable`, analogo per stile e interazione
 * al {@link ScoreDetailModal} che si apre cliccando lo Score.
 */
import { type MouseEvent } from 'react';
import { createPortal } from 'react-dom';
import { Button, Modal } from './ui';
import { LivelloBadge } from './LivelloBadge';
import type { AssessmentView, Livello } from '../shared/dto';

export interface LevelSourcesModalProps {
  /** Riga con i dati della skill (Livelli per fonte + singole revisioni). */
  row: AssessmentView;
  /** Chiude il modale. */
  onClose: () => void;
}

/** Una riga dello schemino: una fonte con il suo Livello. */
interface LevelSourceEntry {
  /** Chiave React univoca. */
  key: string;
  label: string;
  /** Livello da mostrare come badge, oppure `null` se la fonte non ha votato. */
  livello: Livello | null;
}

export function LevelSourcesModal({ row, onClose }: LevelSourcesModalProps) {
  const entries: LevelSourceEntry[] = [];

  // Self: Livello dichiarato in autovalutazione.
  entries.push({ key: 'self', label: 'Il mio', livello: row.livelloSelf });

  // AI: Livello stimato dal quiz.
  entries.push({ key: 'ai', label: 'AI', livello: row.aiLevel });

  // Revisioni: una riga per singolo voto anonimo. Reviewer e Manager sono
  // numerati **per ruolo** quando ce n'è più d'uno, così da distinguere le righe
  // senza rivelare l'identità del valutatore.
  const reviewerCount = row.reviews.filter((r) => r.raterRole === 'reviewer').length;
  const managerCount = row.reviews.filter((r) => r.raterRole === 'manager').length;
  let reviewerIndex = 0;
  let managerIndex = 0;
  for (const review of row.reviews) {
    if (review.raterRole === 'reviewer') {
      reviewerIndex += 1;
      const label = reviewerCount > 1 ? `Reviewer ${reviewerIndex}` : 'Reviewer';
      entries.push({ key: `reviewer-${reviewerIndex}`, label, livello: review.livello });
    } else {
      managerIndex += 1;
      const label = managerCount > 1 ? `Manager ${managerIndex}` : 'Manager';
      entries.push({ key: `manager-${managerIndex}`, label, livello: review.livello });
    }
  }

  const stop = (e: MouseEvent) => e.stopPropagation();

  return createPortal(
    <div onClick={stop} onMouseDown={stop}>
      <Modal
        open
        onClose={onClose}
        title={`Livelli — ${row.skillName}`}
        footer={
          <Button type="button" variant="primary" onClick={onClose}>
            Ho capito
          </Button>
        }
      >
        <p className="livello-guide__intro">
          Tutti i Livelli dichiarati per questa skill, per fonte: il tuo, quello
          stimato dall'AI e le singole revisioni (Reviewer e Manager, in forma
          anonima).
        </p>

        <ul className="level-sources">
          {entries.map((entry) => (
            <li className="level-sources__item" key={entry.key}>
              <span className="level-sources__label">{entry.label}</span>
              <span className="level-sources__value">
                <LivelloBadge livello={entry.livello} label={entry.label} />
              </span>
            </li>
          ))}
        </ul>
      </Modal>
    </div>,
    document.body,
  );
}

export default LevelSourcesModal;
